import { Node, parse } from 'acorn';
import { buildUtilityFromKey } from '../../ai-adapter/utility/utility';

/**
 * The AI "Build the function" flow for an EXECUTE_ENTER_FUNCTION rule action (FE-only — no
 * extension-side change, see the plan). Everything here is pure and synchronous: composing the
 * prompt we send, and parsing what comes back. The actual model call is `aiBridge.chat` in
 * `worker.store.ts`.
 *
 * The function this produces is NOT saved, tested or executed by anything in this repo — it is
 * text a person reviews in the code editor before Save. `checkWorkerFunctionSyntax` only parses
 * it (acorn), never runs it.
 */

/** Markers the model is told to wrap its code in — cheap, unambiguous to extract, and already the
 *  pattern this codebase uses for structured AI output (see `ai-assistant.tsx`'s reply markers). */
const FUNCTION_START = '<<<FUNCTION_START>>>';
const FUNCTION_END = '<<<FUNCTION_END>>>';

/** The exact signature shown in the editor — keep this in sync with `configuration-function-editor.tsx`. */
export const WORKER_FUNCTION_SIGNATURE = 'function applyClaimEdit(payload) {';

// Field guide below is hand-curated from the generated API models (NS-10), not invented (NS-4).
// Typed against the real interfaces so a `tsc` run fails loudly if a field this cites is ever
// renamed or removed from the generated client — see the `satisfies` arrays at the bottom.
const TOP_LEVEL_FIELDS = [
    'claimNarrative — free-text narrative for the claim',
    "subscribers — Array<{ payerId, subscriberId?, memberId?, first?, middle?, last?, dob?, phone?, sex?, ssn?, addresses? }>; subscribers[0] is primary",
    'patient — { patientId?, first?, middle?, last?, dob?, sex?, phones?, emails?, addresses? }',
    'guarantor — { first?, middle?, last?, dob?, phone?, email?, organization? }',
    "providers — Array<{ providerId?, organization?, first?, middle?, last?, npi?, taxonomy?, type }>",
    'facility — { facilityId?, name?, npi?, ein?, addresses? }',
    'seller — { sellerId?, organization?, first?, middle?, last? }',
    'billingProviderId, chargemasterId, claimBundleId, controlNumber, cliaNumber — string ids',
    'primaryPayerAuthorizationNumber, secondaryPayerAuthorizationNumber, tertiaryPayerAuthorizationNumber — string',
    "primaryThirdPartyAdministratorPayerId, secondaryThirdPartyAdministratorPayerId, tertiaryThirdPartyAdministratorPayerId — string",
    'maximumGuarantorResponsibility — { amount: string, currency? } (see chargeAmount below — never a bare number)',
    'services — Array<service line>, see below',
];

const SERVICE_FIELDS = [
    'procedureCode — string, e.g. CPT "99213"',
    'procedureModifierCodes — string[], e.g. ["25"]',
    'diagnosisCodes — string[], e.g. ["M54.5"]',
    'units — number',
    'chargeAmount — { amount: string, currency? }; amount is a STRING like "150.00", never a number',
    'fromDateOfService, throughDateOfService — string dates',
    'placeOfService — string enum, e.g. "OFFICE" | "TELEHEALTH" | "HOME" | "URGENT_CARE_FACILITY"',
    'emergency — boolean',
    'narrative — string',
    'providers, facility — same shapes as the top-level fields above, service-line-specific overrides',
];

export interface WorkerFunctionUtilityArgs {
    /** 'Support' | 'Provider' — which side is authoring this rule; forwarded to `buildUtilityFromKey`. */
    side: string;
}

/**
 * The utility string sent as `context` for the extension's model call (`AiRequest.utility`).
 * Layers the strict output contract on top of the page-purpose block every other AI surface
 * sends (`utility-contexts.json`'s `workerFunction` entry) — same pattern as `ai-assistant.tsx`'s
 * `suggestReply` task appending its own formatting instructions to the base page utility.
 */
export function buildWorkerFunctionUtility({ side }: WorkerFunctionUtilityArgs): string {
    const base = buildUtilityFromKey(`workerFunction:${side}`);

    const contract = [
        'STRICT OUTPUT CONTRACT:',
        `- Write ONLY the statements that go inside: ${WORKER_FUNCTION_SIGNATURE} <your code> }`,
        '- Do not include the signature line or the closing brace yourself.',
        '- The single argument is named `payload` — the claim-edit payload described below.',
        '- Mutate `payload` as needed, then end with `return payload;`.',
        "- To stop processing this claim instead, `throw new Error('reason')` — do not return early with no explanation.",
        '- `payload` has already passed backend validation before this function runs. Do NOT add defensive checks against that — no `if (!payload)`, no `Array.isArray(...)`, no `.length === 0` guards on baseline fields like `payload.services` or `payload.subscribers`. That duplicates validation the backend already did and is code nobody asked for.',
        '- Only branch on a condition when the user’s instructions actually describe one — e.g. "if a same-day procedure line exists" or "skip claims that already have modifier 25" is the requested business logic, not payload validation. Use `.find()` / `.some()` for that, and only handle a "not found" case if the user said what should happen then.',
        '- Change ONLY what the user asked for. Do not add unrelated validation, logging, or error handling.',
        '- Plain synchronous JavaScript that touches ONLY `payload`. Never write any of the following, under any name or indirection: `fetch`, `XMLHttpRequest`, `WebSocket`, `eval`, the `Function` constructor, `require`, `import`, `process`, `global`, `globalThis`, `window`, `document`, `console.*`, `setTimeout`/`setInterval`, or `async`/`await`.',
        '- No unbounded loops — `while (true)`, `for (;;)`, or any loop whose exit condition is not guaranteed to be reached. Iterate over `payload` arrays with `.forEach()` / `.map()` / `.filter()` / `.find()` / a bounded `for` over a known-length array instead.',
        '',
        'PAYLOAD SHAPE (top level):',
        ...TOP_LEVEL_FIELDS.map(line => `  ${line}`),
        '',
        'PAYLOAD SHAPE (each entry of payload.services):',
        ...SERVICE_FIELDS.map(line => `  ${line}`),
        '',
        `Wrap your code between ${FUNCTION_START} and ${FUNCTION_END} on their own lines, exactly once.`,
        'You may add one short sentence explaining what you wrote OUTSIDE the markers — nothing else outside them.',
    ];

    return `${base}\n\n${contract.join('\n')}`;
}

export interface WorkerFunctionRuleContext {
    /** What the rule's WHEN conditions match on, e.g. "Payment: CARC CO · code 97" — already formatted. */
    matchSummary: string;
    ruleName: string;
    /** 'denial' | 'rejection' automation worker id. */
    workerId: string;
}

/** Context JSON for the request — what the rule fires on, so the model can tailor guards to it. */
export function buildWorkerFunctionContextJson(context: WorkerFunctionRuleContext): string {
    return JSON.stringify(context);
}

const FENCED_CODE_BLOCK = /```(?:js|javascript)?\s*\n?([\s\S]*?)```/i;
const SIGNATURE_WRAPPER = /^\s*function\s+\w+\s*\([^)]*\)\s*\{\s*([\s\S]*?)\s*\}\s*;?\s*$/;

/**
 * Pulls the function body out of the model's reply. Tries the markers first (the contract we
 * asked for); falls back to a fenced code block, since models drift from instructions. Returns
 * `null` — never an empty string — when nothing usable was found, so the caller can tell "no code"
 * from "empty function" (`getExecuteFunctionValidationError` already means the latter).
 */
export function extractGeneratedFunction(replyText: string): string | null {
    const markerMatch = replyText.match(
        new RegExp(`${FUNCTION_START}\\s*\\n?([\\s\\S]*?)\\n?\\s*${FUNCTION_END}`),
    );
    let body = markerMatch?.[1];

    if (body === undefined) {
        const fenced = replyText.match(FENCED_CODE_BLOCK);
        body = fenced?.[1];
    }

    if (body === undefined) {
        return null;
    }

    const trimmed = body.trim();
    if (!trimmed) {
        return null;
    }

    // If the model included the signature/wrapper despite being told not to, strip it back to a body.
    const wrapped = trimmed.match(SIGNATURE_WRAPPER);
    return wrapped ? wrapped[1].trim() : trimmed;
}

/**
 * Syntax check only — NEVER executes the code (no `eval`/`new Function` call anywhere in this
 * file). Wraps as a function body so a bare top-level `return payload;`, as every real rule has,
 * is valid to parse standalone.
 */
export function checkWorkerFunctionSyntax(code: string): string | null {
    if (!code.trim()) {
        return null;
    }

    try {
        parse(`(${WORKER_FUNCTION_SIGNATURE}\n${code}\n})`, { ecmaVersion: 'latest' });
        return null;
    } catch (error) {
        const message = error instanceof Error ? error.message : 'Syntax error.';
        return message.replace(/\s*\(\d+:\d+\)\s*$/, '');
    }
}

/**
 * NOT a sandbox and not a security boundary (PR review, 2026-09-28) — this is a best-effort,
 * static check against direct, unobfuscated use of a fixed name list, the same limitation the
 * AI prompt's instructions already have. `payload['fe' + 'tch']`, reaching a global through some
 * other property chain, or any other indirection walks straight past it. Real enforcement has to
 * live at the execution boundary (the backend), which this repo cannot reach or verify (NS-12).
 * This exists to catch the direct, common case — including a hand-typed line, not just AI output —
 * before Save, not to make the feature safe to run unobserved.
 */
const FORBIDDEN_GLOBAL_NAMES = new Set([
    'fetch',
    'XMLHttpRequest',
    'WebSocket',
    'eval',
    'Function',
    'require',
    'process',
    'global',
    'globalThis',
    'window',
    'document',
    'console',
    'setTimeout',
    'setInterval',
    'setImmediate',
]);

type AstNode = Node & Record<string, unknown>;

/**
 * A loop test that provably never becomes false without evaluating anything at runtime — only a
 * literal (`true`, `1`, `"x"`, …), judged by ordinary JS truthiness. Deliberately narrow: no
 * constant folding of expressions like `!false` or `1 + 1`, since a false NEGATIVE here (missing
 * a truthy test written some other way) is the safe direction to be wrong in for a check whose
 * false POSITIVE would block a legitimate loop from saving at all.
 */
function isStaticallyTruthyTest(node: AstNode | null | undefined): boolean {
    if (!node || node.type !== 'Literal') {
        return false;
    }
    return Boolean((node as unknown as { value: unknown }).value);
}

/** Generic ESTree walk: acorn nodes are plain nested objects/arrays, keyed by whatever each node
 *  type happens to carry — no separate walker package needed for a check this narrow. Threads the
 *  immediate parent's type/key/computed-ness down to `visit` so it can tell a value REFERENCE
 *  apart from a binding, a property key, or a label — see `isNonReferenceIdentifierPosition`. */
function walkAst(
    value: unknown,
    visit: (node: AstNode, parentType: string | null, key: string | null, parentComputed: boolean) => void,
    parentType: string | null = null,
    key: string | null = null,
    parentComputed = false,
): void {
    if (!value || typeof value !== 'object') {
        return;
    }
    if (Array.isArray(value)) {
        for (const item of value) {
            walkAst(item, visit, parentType, key, parentComputed);
        }
        return;
    }
    const node = value as AstNode;
    if (typeof node.type === 'string') {
        visit(node, parentType, key, parentComputed);
    }
    const computed = node.computed === true;
    for (const childKey of Object.keys(node)) {
        if (childKey === 'type' || childKey === 'start' || childKey === 'end' || childKey === 'loc' || childKey === 'range') {
            continue;
        }
        walkAst(node[childKey], visit, (node.type as string) ?? null, childKey, computed);
    }
}

/**
 * True for every ESTree position where an `Identifier` node is a BINDING, a plain KEY, or a
 * LABEL — never a read of the name's value — so `checkWorkerFunctionForbiddenPatterns` can tell
 * `const fetch = 1;` (declares a harmless local, never checked) from `const fn = fetch;` (reads
 * the real global, must be checked) using only immediate parent context, no full scope analysis.
 *
 * Known, accepted trade-off in the OTHER direction: a destructuring binding that happens to share
 * a forbidden name (`const { window } = payload;`) is NOT excluded here, so it gets (incorrectly)
 * flagged as if it read the global. That is a false positive — the safe direction to be wrong in
 * for a Save-blocking check — and `payload` has no field named after any entry in
 * `FORBIDDEN_GLOBAL_NAMES` (see the field guide above), so it is not expected to come up for real.
 */
function isNonReferenceIdentifierPosition(parentType: string, key: string, parentComputed: boolean): boolean {
    if (parentType === 'Property' && key === 'key' && !parentComputed) {
        return true; // `{ window: 1 }` — the key name, not a read of `window`.
    }
    if (parentType === 'MemberExpression' && key === 'property' && !parentComputed) {
        return true; // `payload.window` — a field name, not a read of `window`.
    }
    if (parentType === 'VariableDeclarator' && key === 'id') {
        return true; // `const fetch = 1;` — declares a local, never a read.
    }
    if (
        (parentType === 'FunctionDeclaration' ||
            parentType === 'FunctionExpression' ||
            parentType === 'ClassDeclaration' ||
            parentType === 'ClassExpression') &&
        key === 'id'
    ) {
        return true; // a function/class named `fetch` — its own declared name, not a call to it.
    }
    if (parentType === 'CatchClause' && key === 'param') {
        return true; // `catch (window) {}` — the caught error's local name.
    }
    if (
        (parentType === 'FunctionDeclaration' ||
            parentType === 'FunctionExpression' ||
            parentType === 'ArrowFunctionExpression') &&
        key === 'params'
    ) {
        return true; // `function f(window) {}` — a parameter name.
    }
    if (parentType === 'AssignmentPattern' && key === 'left') {
        return true; // `function f(window = 1) {}` — still the parameter name, not the default value.
    }
    if (
        (parentType === 'LabeledStatement' || parentType === 'BreakStatement' || parentType === 'ContinueStatement') &&
        key === 'label'
    ) {
        return true; // loop labels live in a separate namespace from variables entirely.
    }
    return false;
}

/**
 * Scope-aware only to the extent above — a per-node parent check, not full binding resolution.
 * `this.fetch(...)` (a sloppy-mode implicit-global access via `this`, not an `Identifier` at all)
 * is a known, still-open gap alongside the aliasing one already documented on the exported
 * function below; neither is closed by this pass.
 */
function checkForbiddenIdentifier(node: AstNode, parentType: string | null, key: string | null, parentComputed: boolean): string | null {
    if (node.type !== 'Identifier') {
        return null;
    }
    const name = node.name as string;
    if (!FORBIDDEN_GLOBAL_NAMES.has(name)) {
        return null;
    }
    if (parentType !== null && key !== null && isNonReferenceIdentifierPosition(parentType, key, parentComputed)) {
        return null;
    }
    return name;
}

/**
 * Flags a REFERENCE to a forbidden name wherever one can appear as a value — a call (e.g. calling
 * `fetch`), `new` (`new WebSocket(...)`), member access (`window.foo`, `window['foo']`), a bare assignment
 * (`const fn = fetch;` — the exact gap a PR review caught on 2026-09-28), a return value, an
 * argument passed to something else, and so on — by checking every `Identifier` node against its
 * immediate parent position rather than special-casing each syntactic shape separately.
 */
export function checkWorkerFunctionForbiddenPatterns(code: string): string | null {
    if (!code.trim()) {
        return null;
    }

    let program: AstNode;
    try {
        program = parse(`(${WORKER_FUNCTION_SIGNATURE}\n${code}\n})`, { ecmaVersion: 'latest' }) as unknown as AstNode;
    } catch {
        // Not this function's job — checkWorkerFunctionSyntax reports parse errors.
        return null;
    }

    let found: string | null = null;
    const report = (name: string): void => {
        found = found ?? name;
    };

    walkAst(program, (node, parentType, key, parentComputed) => {
        if (found) {
            return;
        }

        const identifierHit = checkForbiddenIdentifier(node, parentType, key, parentComputed);
        if (identifierHit) {
            report(identifierHit);
            return;
        }

        switch (node.type) {
            case 'CallExpression':
            case 'NewExpression': {
                // `import(...)`'s callee has its own node type, not an Identifier — the general
                // check above cannot see it, so it needs this one explicit case.
                const callee = node.callee as AstNode | undefined;
                if (callee?.type === 'Import') {
                    report('import');
                }
                break;
            }
            case 'ImportExpression':
                report('import');
                break;
            case 'AwaitExpression':
                report('await');
                break;
            case 'WhileStatement':
                if (isStaticallyTruthyTest(node.test as AstNode | undefined)) {
                    report('while (true)');
                }
                break;
            case 'DoWhileStatement':
                if (isStaticallyTruthyTest(node.test as AstNode | undefined)) {
                    report('do…while (true)');
                }
                break;
            case 'ForStatement':
                // `node.test === null` is `for (;;)` — no test at all, not just a falsy one.
                if (node.test === null || isStaticallyTruthyTest(node.test as AstNode | undefined)) {
                    report('for (;;)');
                }
                break;
            default:
                break;
        }

        if (
            node.async === true &&
            (node.type === 'FunctionDeclaration' ||
                node.type === 'FunctionExpression' ||
                node.type === 'ArrowFunctionExpression')
        ) {
            report('async function');
        }
    });

    if (!found) {
        return null;
    }

    if (found === 'while (true)' || found === 'do…while (true)' || found === 'for (;;)') {
        return `Remove the unbounded loop (\`${found}\`) — every loop must be guaranteed to end.`;
    }
    if (found === 'async function' || found === 'await') {
        return "Remove `async`/`await` — this function must be plain synchronous JavaScript.";
    }
    if (found === 'import') {
        return 'Remove the dynamic `import(...)` — nothing may be loaded from outside `payload`.';
    }
    return `Remove \`${found}\` — this function may only read and write \`payload\`.`;
}

/**
 * The editor shows the signature and closing brace as ordinary, editable text — one continuous
 * code block, not code sandwiched between two frozen labels — because the wire contract only
 * ever stores the BODY (`RuleAction.value.function`); the wrapper is entirely cosmetic. These two
 * functions are exact inverses of each other for a well-formed body (round-trip tested), so a
 * controlled editor that re-wraps on every keystroke does not fight the user's cursor.
 *
 * Whatever the user types on the signature / closing-brace lines never changes what is stored or
 * what argument name the backend binds — that is always `payload`, confirmed against the account's
 * own working rule (2026-09-24). `extractFunctionBodyFromEditorText` deliberately ignores the
 * signature line's actual text; it only needs SOME `{` before the body and the final `}` we always
 * append.
 */
export function wrapFunctionBodyForEditor(body: string): string {
    return `${WORKER_FUNCTION_SIGNATURE}\n${body}\n}`;
}

/**
 * Inverse of `wrapFunctionBodyForEditor`. Finds the first `{` (the signature's, wherever the user
 * left it) and takes everything after it as the body — up to the LAST `}` when one exists after
 * it (always the one closing brace we ourselves append at the very end, however many braces the
 * body itself contains, balanced or not mid-edit), or to the end of the text otherwise.
 *
 * That "otherwise" matters: falling back to the WHOLE text (signature included) when the closing
 * brace is merely missing was the bug (PR review, 2026-09-28) — this is a controlled editor, so
 * the stored value gets re-wrapped via `wrapFunctionBodyForEditor` on the very next render, and a
 * body that still starts with `function applyClaimEdit(payload) {` duplicates that line every time,
 * corrupting the function instead of letting the user finish typing the brace back in.
 *
 * Only when there is no opening brace AT ALL — nothing to anchor on — does this fall back to the
 * whole text, and even then never drops what the user typed; `checkWorkerFunctionSyntax` flags the
 * result either way.
 */
export function extractFunctionBodyFromEditorText(editorText: string): string {
    const openIndex = editorText.indexOf('{');
    if (openIndex === -1) {
        return editorText;
    }

    const closeIndex = editorText.lastIndexOf('}');
    const end = closeIndex > openIndex ? closeIndex : editorText.length;

    let body = editorText.slice(openIndex + 1, end);
    if (body.startsWith('\n')) {
        body = body.slice(1);
    }
    if (body.endsWith('\n')) {
        body = body.slice(0, -1);
    }
    return body;
}
